import { useMemo, useCallback, useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCancel,
  faEdit,
  faSave,
  faCheck
} from "@fortawesome/free-solid-svg-icons";
import {
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import { default as axios } from "../axiosConfig";
import useFetchHolidays from "../Hooks/useFetchHolidays";
import {
  API_GetTimesheet,
  API_SaveTimesheet,
  API_GetCompanyIDByJob,
  API_GetCompanyInfo,
  API_GetJobContract,
  API_GetCandidateByID,
  API_GetRegion,
  API_GetCompanyPosting,
  API_GetHMInfo,
  API_GetApprovedLeaveRequests,
  API_GetAdminIDByEmail,
  API_RejectTimesheet
} from "../Assets/ApiUrlContainer";
import UserContext from "../Contexts/UserContext";

const TimesheetDetails = ({ role, timesheet, email, managerId, onBack }) => {
  const currentUser = useContext(UserContext);
  const [userIsTGSAdmin, setUserIsTGSAdmin] = useState(false);
  const [timesheetFullyApproved, setTimesheetFullyApproved] = useState(false);

  const [entry, setEntry] = useState(null);
  const [location, setLocation] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [submittedDate, setSubmittedDate] = useState(
    timesheet.SubmittedDate || null
  );
  const [timesheetState, setTimesheetState] = useState(timesheet);
  const [dates, setDates] = useState([]);
  const [companyName, setCompanyName] = useState("Loading...");
  const [managerName, setManagerName] = useState("Loading...");
  const [employeeName, setEmployeeName] = useState("Loading...");
  const [employeeNotes, setEmployeeNotes] = useState(
    timesheet?.EmployeeNotes || ""
  );
  const [employerNotes, setEmployerNotes] = useState(
    timesheet?.EmployerNotes || ""
  );
  const [employerError, setEmployerError] = useState("");
  const [employeeError, setEmployeeError] = useState("");
  const [submitting, setSubmitting] = useState(false); // Show confirmation message
  const isSubmitted = !!submittedDate;
  const isApprovedByManager = !!timesheet?.ApprovingHiringManagerId;
  const isApprovedByAdmin = !!timesheet?.ApprovingTgsManagerId;

  const canEdit = useMemo(() => {
    return (
      role === "employee" &&
      (!!timesheetState.RejectedBy || // allow edit if rejected
        !timesheetState?.SubmittedDate) // allow edit if not yet submitted
    );
  }, [role, timesheetState]);

  const [hiringManagerID, setHiringManagerID] = useState(null);

  const [billingRate, setBillingRate] = useState(37.5);
  const [maxDailyHours, setMaxDailyHours] = useState(7.5);
  const [maxWeeklyHours, setMaxWeeklyHours] = useState(40);
  const [approveRejectSuccess, setApproveRejectSuccess] = useState("");
  const [timeoff, setTimeoff] = useState([]);
  const [timeoffMap, setTimeoffMap] = useState([]);
  const { holidays } = useFetchHolidays(location);

  const getStatusColor = (status) => {
    switch (status) {
      case "Rejected by Hiring Manager":
        return "red";
      case "Approved by Admin":
        return "green";
      case "Approved by Hiring Manager":
        return "#2196F3"; // blue
      case "Pending Approval":
        return "#FF9800"; // orange
      default:
        return "gray";
    }
  };

  const getStatusText = () => {
    if (timesheetState.RejectedBy) {
      return `Rejected by ${timesheetState.RejectedBy}`;
    }

    if (timesheetState.ApprovingHiringManagerId && timesheetState.ApprovingTgsManagerId) {
      return "Approved";
    }

    if (timesheetState.ApprovingHiringManagerId) {
      return "Approved by Hiring Manager";
    }

    if (submittedDate) {
      return "Pending Approval";
    }

    return "Pending Submission";
  };

  const isHoliday = useCallback(
    (date) => {
      const formatted = dayjs(date).format("YYYY-MM-DD");
      const match = holidays.find((h) => h.date === formatted);
      return match?.name || null;
    },
    [holidays]
  );

  const fetchCandidateInfo = useCallback(async () => {
    try {
      const candidateResponse = await axios.get(`${API_GetCandidateByID}/${timesheet.CandidateId}`);
      setEmployeeName(candidateResponse.data.Name || "Unknown Employee");

      const companyIDResponse = await axios.get(`${API_GetCompanyIDByJob}/${timesheet.JobId}`);
      const companyID = companyIDResponse.data.CompanyId;

      const companyInfoResponse = await axios.get(`${API_GetCompanyInfo}/${companyID}`);
      const companyInfo = companyInfoResponse.data;
      setCompanyName(companyInfo?.Name || "Unknown Company");

      const companyPostingResponse = await axios.get(`${API_GetCompanyPosting}/${timesheet.JobId}`);
      const managerId = companyPostingResponse.data.ManagerId;

      if (role === "hiring manager") {
        setHiringManagerID(managerId);
      }

      const HMInfoResponse = await axios.get(`${API_GetHMInfo}/${managerId}`);
      setManagerName(HMInfoResponse.data.Name);

      const employeeLocationResponse = await axios.get(`${API_GetRegion}/${timesheet.CandidateId}`);
      setLocation(employeeLocationResponse.data.region);

      const approvedTimeoff = await axios.get(`${API_GetApprovedLeaveRequests}/${timesheet.CandidateId}`);
      setTimeoff(approvedTimeoff.data);

    } catch (error) {
      console.error("Error fetching candidate info:", error);
    }
  }, [timesheet.JobId, timesheet.CandidateId]);

  const convertDateForDayJS = (dateToConvert) => {
    let date = new Date(dateToConvert);
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(-2)}-${("0" + date.getUTCDate()).slice(-2)}`;
  }

  const fetchTimesheetDetails = useCallback(
    async (startDate) => {
      try {
        const response = await axios.get(API_GetTimesheet, {
          params: {
            CandidateId: timesheet.CandidateId,
            StartDate: dayjs(convertDateForDayJS(startDate)).format("YYYY-MM-DD"),
            JobId: timesheet.JobId,
          },
        });

        if (response.data.timesheet) {
          const content = response.data.timesheet.Entries ? response.data.timesheet.Entries[0] : {
            name: "No Data",
            hours: Array(7).fill(0),
          };

          setEntry(content);
          setTimesheetState(response.data.timesheet);
          setDates(generateWeekDays(response.data.timesheet.WeekStartDate));
          setSubmittedDate(response.data.timesheet.SubmittedDate || null);
          setEmployeeNotes(response.data.timesheet.EmployeeNotes || "");
          setEmployerNotes(response.data.timesheet.EmployerNotes || "");
        }

        const res = await axios.get(API_GetJobContract, {
          params: {
            CandidateId: timesheet.CandidateId,
            JobId: timesheet.JobId,
          },
        });

        const contract = res.data;
        setBillingRate(contract.ContractorCost);
        setMaxWeeklyHours(contract.WeeklyHoursTarget);
        setMaxDailyHours(contract.DailyHoursLimit);
      } catch (error) {
        console.error("Error fetching timesheet details:", error);
      }
    },
    [timesheet, setSubmittedDate, setEmployeeNotes, setDates]
  );

  // useEffect(() => {
  //   const fetchHiringManagerID = async () => {
  //     if (role === "hiring manager" && email) {
  //       try {
  //         const res = await axios.get(API_GetHiringManagerByEmail, {
  //           params: { email },
  //         });

  //         if (res.data && res.data.ManagerId) {
  //           setHiringManagerID(res.data.ManagerId);
  //           console.log("hiringManagerID fetched:", res.data.ManagerId);
  //         }
  //       } catch (err) {
  //         console.error("Failed to fetch hiring manager Id:", err);
  //       }
  //     }
  //   };
  //   fetchHiringManagerID();
  // }, [role, email]);

  useEffect(() => {
    const fetchAdminID = async () => {
      if (role === "tgs-admin" && email) {
        try {
          const res = await axios.get(API_GetAdminIDByEmail, {
            params: { email },
          });

          if (res.data && res.data.adminId) {
            setHiringManagerID(res.data.adminId);
            console.log("adminId fetched:", res.data.adminId);
          }
        } catch (err) {
          console.error("Failed to fetch admin Id:", err);
        }
      }
    };

    fetchAdminID();
  }, [role, email]);

  useEffect(() => {
    const weekStart = dayjs(convertDateForDayJS(timesheet.WeekStartDate));
    const weekEnd = weekStart.add(6, "day");

    const parsedMap = {};

    for (const req of timeoff) {
      const hoursMap = JSON.parse(req.HoursOffPerDay || "{}");

      for (const [mmdd, hrs] of Object.entries(hoursMap)) {
        const date = dayjs(`${weekStart.year()}-${mmdd}`);
        if (date.isSameOrAfter(weekStart) && date.isSameOrBefore(weekEnd)) {
          parsedMap[date.format("YYYY-MM-DD")] = Number(hrs);
        }
      }
    }

    setTimeoffMap(parsedMap);
  }, [timeoff, timesheet.WeekStartDate]);

  useEffect(() => {
    if (email) {
      fetchCandidateInfo();
      fetchTimesheetDetails(timesheet.WeekStartDate, email);
    }
  }, [
    email,
    timesheet.WeekStartDate,
    fetchCandidateInfo,
    fetchTimesheetDetails,
  ]);

  useEffect(() => {
    setUserIsTGSAdmin(currentUser.user.IsAdmin && currentUser.user.IsGoogleUser);
    setTimesheetFullyApproved(!!timesheet.ApprovingHiringManagerId && !!timesheet.SubmittedDate && !!timesheet.ApprovingTgsManagerId);
  }, [timesheet, currentUser, setUserIsTGSAdmin]);

  function generateWeekDays(start) {
    return Array.from({ length: 7 }, (_, i) =>
      dayjs(convertDateForDayJS(start)).add(i, "day").format("ddd, MMM D")
    );
  }

  useEffect(() => {
    console.log("hiringManagerID:", hiringManagerID);
  }, [hiringManagerID]);

  const handleSaveTimesheet = async (isSubmitting = false, isApproving = false) => {
    console.log("handleSaveTimesheet triggered", {
      isApproving,
      managerId,
      role,
    });

    if (isSaving) return; // prevent double-submits
    setIsSaving(true);

    if (isProcessing) return; // prevent spam clicking
    setIsProcessing(true);

    try {
      setEmployerError("");
      setEmployeeError("");

      if (role === "hiring manager" && !isApproving && !employerNotes.trim()) {
        setEmployerError("Notes section cannot be blank if rejecting timesheet!");
        setIsSaving(false);
        setIsProcessing(false);
        return;
      }

      const { hasError, message } = getErrorState();
      if (hasError && !currentUser.user.IsAdmin) {
        setEmployeeError(message);
        setIsSaving(false);
        setIsProcessing(false);
        return;
      }

      if (hiringManagerID && isApproving) {
        let payload = {
          ...timesheet,
          StartDate: dayjs(convertDateForDayJS(timesheet.WeekStartDate)).format("YYYY-MM-DD"),
          EndDate: dayjs(convertDateForDayJS(timesheet.WeekEndDate)).format("YYYY-MM-DD"),
          JobId: timesheet.JobId,
          Entries: entry ? [{
            ...entry,
            hours: entry.hours.map((h) => {
              const num = parseFloat(h);
              return isNaN(num) ? 0 : Math.max(0, num);
            }),
          }] : [],
          Metadata: { ...timesheet.Metadata, billingRate: billingRate },
          EmployerNotes: employerNotes,
          SubmittedDate: timesheet.SubmittedDate ? dayjs(convertDateForDayJS(timesheet.SubmittedDate)).format("YYYY-MM-DD") : null,
        };

        // Dynamically set approver field
        if (role === "tgs-admin") {
          payload.ApprovingTgsManagerId = hiringManagerID;
          payload.ApprovingHiringManagerId = timesheet.ApprovingHiringManagerId || null;
        } else if (role === "hiring manager") {
          payload.ApprovingHiringManagerId = hiringManagerID;
          payload.ApprovingTgsManagerId = timesheet.ApprovingTgsManagerId || null;
        }

        // Send request
        console.log("payload being sent:", payload);
        await axios.put(API_SaveTimesheet, payload);

      } else if (hiringManagerID && !isApproving) {
        await axios.post(`${API_RejectTimesheet}/${timesheet.Id}`, {
          notes: employerNotes,
          rejectedBy: role === "tgs-admin" ? "TGS Admin" : "Hiring Manager",
        });

        setApproveRejectSuccess("Successfully rejected timesheet.");
        await fetchCandidateInfo();
        await fetchTimesheetDetails(timesheet.WeekStartDate);
        setTimeout(() => setApproveRejectSuccess(""), 3000);

      } else {
        // if employee is saving / submitting
        await axios.put(API_SaveTimesheet, {
          ...timesheet,
          StartDate: dayjs(convertDateForDayJS(timesheet.WeekStartDate)).format("YYYY-MM-DD"),
          EndDate: dayjs(convertDateForDayJS(timesheet.WeekEndDate)).add("6", "day").format("YYYY-MM-DD"),
          JobId: timesheet.JobId,
          Entries: entry ? [{
            ...entry,
            hours: entry.hours.map((h) => {
              const num = parseFloat(h);
              return isNaN(num) ? 0 : Math.max(0, num); // Allow values above maxDailyHours
            })
          }] : [],
          Metadata: { ...timesheet.Metadata, billingRate: billingRate },
          SubmittedDate: isSubmitting ? dayjs().format("YYYY-MM-DD") : null, // Reset if not submitting
          EmployeeNotes: employeeNotes || "",
        });
      }

      if (isSubmitting) {
        const submittedAt = dayjs().format("YYYY-MM-DD");

        setSubmittedDate(submittedAt); // still update local date
        setTimesheetState((prev) => ({
          ...prev,
          SubmittedDate: submittedAt,
          RejectedBy: null, // optional: clear rejection when re-submitting
        }));

        setSubmitting(true);
        setTimeout(() => setSubmitting(false), 3000);

      } else {
        setSubmittedDate(null); // Remove submission date if just saving
      }

      // Re-fetch candidate and timesheet info after approval/rejection
      await fetchCandidateInfo();
      await fetchTimesheetDetails(timesheet.WeekStartDate);
      setIsProcessing(false);
      setIsEditing(false);
      setIsSaving(false);

    } catch (error) {
      setIsProcessing(false);
      setIsSaving(false);
      console.error("Error saving timesheet:", error);
    }
  };

  const getErrorState = () => {
    if (!entry) return { hasError: false };

    const hours = entry.hours.map((h) => parseFloat(h) || 0);
    const hasHolidayOrWeekendHours = hours.some((hour, hourIndex) => {
      const day = dayjs(convertDateForDayJS(timesheet.WeekStartDate)).add(hourIndex, "day");
      const isWeekend = day.day() === 6 || day.day() === 0;
      const isHolidayDay = !!isHoliday(day);
      return (isWeekend || isHolidayDay) && hour > 0;
    });

    const hasWeeklyOvertime = hours.reduce((sum, val) => sum + val, 0) > maxWeeklyHours;
    const hasDailyOvertime = hours.some((hour) => hour > maxDailyHours);
    const hasOvertime = hasWeeklyOvertime || hasDailyOvertime;

    if ((hasHolidayOrWeekendHours || hasOvertime) && !employeeNotes.trim()) {
      return {
        hasError: true,
        message:
          "Notes section must not be blank if weekend, holiday, or overtime hours are logged!",
      };
    }

    return { hasError: false };
  };

  const handleInputChange = (hourIndex, value) => {
    // Allow empty string while typing
    if (value === "") {
      const updatedEntry = { ...entry };
      updatedEntry.hours[hourIndex] = "";
      setEntry(updatedEntry);
      setEmployeeError("");
      return;
    }

    let numericValue = parseFloat(value);

    // If invalid number (e.g., user typed a letter), ignore
    if (isNaN(numericValue)) return;

    // Keep above 0
    numericValue = Math.max(0, numericValue);

    const updatedEntry = { ...entry };
    updatedEntry.hours[hourIndex] = numericValue.toString();
    setEntry(updatedEntry);

    const { hasError, message } = getErrorState();
    setEmployeeError(hasError ? message : "");
  };

  const grandTotal = entry ? entry.hours.reduce((sum, val) => sum + (parseFloat(val) || 0), 0).toFixed(1) : "0.0";
  const totalBilling = parseFloat(grandTotal) * billingRate;

  const readyForApprovalRejection = useMemo(() => {
    const isRejected = !!timesheetState.RejectedBy;

    return (
      !isRejected &&
      ((role === "hiring manager" &&
        !timesheetState.ApprovingHiringManagerId) ||
        (role === "tgs-admin" &&
          timesheetState.ApprovingHiringManagerId &&
          !timesheetState.ApprovingTgsManagerId))
    );
  }, [role, timesheetState]);

  // console.log("DEBUG - Approval Check", {
  //   role,
  //   approvingHMID: timesheetState.ApprovingHiringManagerId,
  //   approvingTGSID: timesheetState.ApprovingTgsManagerId,
  //   readyForApprovalRejection,
  // });

  return (
    <div className="">
      <div className="tasksContent">
        <div className="actionBar">
          <Button
            variant="outlined"
            sx={{ borderColor: "#673ab7", color: "#673ab7" }}
            onClick={onBack}
          >
            Back to All Timesheets
          </Button>
          <div className="tasksBtnContainer">
            {/* if manager on submitted sheet, approval and rejection buttons */}
            {/* if employee, edit/save buttons */}
            {timesheetState.SubmittedDate && readyForApprovalRejection ? (
              <>
                <Tooltip title="Approve as the hiring manager or admin, depending on your role.">
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ marginRight: "5px" }}
                    onClick={() => {
                      handleSaveTimesheet(false, true);
                    }}
                    disabled={isProcessing || isSaving}
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ marginRight: "5px" }}
                    />
                    Approve Timesheet
                  </Button>
                </Tooltip>

                {(role === "hiring manager" || role === "tgs-admin") && (
                  <Tooltip title="Reject this timesheet and leave a reason.">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleSaveTimesheet(false, false);
                      }}
                      disabled={isProcessing || isSaving}
                    >
                      <FontAwesomeIcon
                        icon={faCancel}
                        style={{ marginRight: "5px" }}
                      />
                      Reject Timesheet
                    </Button>
                  </Tooltip>
                )}

                {approveRejectSuccess && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    {approveRejectSuccess}
                  </Alert>
                )}
              </>
            ) : null}
          </div>
        </div>
        {/* Employer Notes Section */}
        {(timesheet.EmployerNotes || managerId) && (
          <div className="notesSection">
            <h4>Manager Notes</h4>
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Add any additional comments here..."
              value={employerNotes}
              onChange={(e) => {
                setEmployerNotes(e.target.value);
                if (e.target.value.trim()) {
                  setEmployerError("");
                }
              }}
              variant="outlined"
              disabled={!managerId || !timesheet.SubmittedDate || timesheetState.RejectedBy}
            />
            {employerError && !employerNotes && (
              <p style={{ color: "red", marginTop: "5px" }}>{employerError}</p>
            )}
          </div>
        )}

        {/* Employee Info Section */}
        <div className="headerContainer">
          <div className="employeeInfo">
            <p>
              <strong>Name:</strong> {employeeName}
            </p>
            <p>
              <strong>Company:</strong> {companyName}
            </p>
            <p>
              <strong>Timeframe:</strong> {dates[0]} - {dates[6]}
            </p>
            <p>
              <strong>Actual Hours:</strong> {grandTotal}
            </p>
            <p>
              <strong>Submission Date:</strong>{" "}
              {submittedDate ? dayjs(convertDateForDayJS(submittedDate)).format("MMM D, YYYY") : "N/A"}
            </p>
          </div>
          <div className="projectInfo">
            <p>
              <strong>Job:</strong> {entry?.name || null}
            </p>
            <p>
              <strong>Maximum Hours Per Day:</strong> {maxDailyHours || null}
            </p>
            <p>
              <strong>Manager Name:</strong> {managerName}
            </p>
            <p>
              <strong>Expected Hours:</strong>{" "}
              {Number(maxWeeklyHours).toFixed(1)}
            </p>
            <p>
              <strong>Status: </strong>
              <span
                style={{
                  fontWeight: "bold",
                  color: getStatusColor(getStatusText()),
                }}
              >
                {getStatusText()}
              </span>
            </p>
          </div>
        </div>

        <TableContainer component={Paper} className="timesheetTableContainer">
          <Table sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "50%" }}>
                  <strong>Day</strong>
                </TableCell>
                <TableCell sx={{ width: "50%" }}>
                  <strong>Hours</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entry &&
                entry.hours.map((hour, i) => {
                  const day = dayjs(convertDateForDayJS(timesheet.WeekStartDate)).add(i, "day");
                  const isWeekendDay = day.day() === 6 || day.day() === 0;
                  const holidayName = isHoliday(day);
                  const isHolidayDay = !!holidayName;
                  const isNonZero = parseFloat(hour) > 0;
                  const isOvertime = parseFloat(hour) > maxDailyHours;

                  let bgColor = "inherit";
                  let textColor = "inherit";

                  const isTimeOffDay = !!timeoffMap[day.format("YYYY-MM-DD")];

                  if (isHolidayDay || isWeekendDay || isTimeOffDay) {
                    bgColor = isNonZero ? "#FF474C" : "lightgrey"; // red for filled holiday, gray for blank
                    textColor = isNonZero ? "white" : "inherit";
                  } else {
                    bgColor = isOvertime ? "#FF474C" : "inherit"; // red for filled holiday, gray for blank
                    textColor = isOvertime ? "white" : "inherit";
                  }

                  return (
                    <TableRow key={i}>
                      {/* First column: Display day */}
                      <TableCell>
                        {day.format("ddd, MMM D")}
                        {holidayName ? ` (${holidayName})` : ""}
                        {isTimeOffDay ? " (Time Off)" : ""}
                      </TableCell>
                      {/* Second column: Display hours */}
                      <TableCell
                        align="center"
                        sx={{ backgroundColor: bgColor, color: textColor }}
                      >
                        {isEditing ? (
                          <TextField
                            type="number"
                            value={hour === "" ? "" : String(hour)}
                            onChange={(e) =>
                              handleInputChange(i, e.target.value)
                            }
                            variant="outlined"
                            size="small"
                            disabled={!!timeoffMap[day.format("YYYY-MM-DD")]}
                            sx={{
                              backgroundColor: !!holidayName || isWeekendDay || !!timeoffMap[day.format("YYYY-MM-DD")] ? "#f0f0f0" : "white",
                            }}
                          />
                        ) : (
                          (parseFloat(hour) || 0).toFixed(1)
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Employee Notes Section */}
        <div className="notesSection">
          <h4>Notes</h4>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Add any additional comments here..."
            value={employeeNotes}
            onChange={(e) => {
              setEmployeeNotes(e.target.value);
              if (e.target.value.trim()) {
                setEmployeeError("");
              }
            }}
            variant="outlined"
            disabled={!isEditing}
          />
          {employeeError && (
            <p style={{ color: "red", marginTop: "5px" }}>{employeeError}</p>
          )}
        </div>

        <div className="billingSummary">
          {role !== "employee" && role !== "candidate" && (
            <p>
              <strong>Billing rate (hourly):</strong> $
              {Number(billingRate).toFixed(2)}
            </p>
          )}
          <p className="totalBilling">
            <strong>Total Billing:</strong>${Number(totalBilling).toFixed(2)}
          </p>
        </div>

        {/* Submit Button (show only if not manager) */}
        {!managerId && (
          <div
            className="timesheetBtnContainer"
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            {canEdit && (
              <>
                {role === "employee" && canEdit && (
                  <Button
                    variant="contained"
                    color={isEditing ? "success" : "primary"}
                    onClick={() => {
                      if (isEditing) {
                        const { hasError, message } = getErrorState();
                        if (hasError) {
                          setEmployeeError(message);
                          return;
                        }
                        handleSaveTimesheet(false);
                      }
                      setIsEditing(!isEditing);
                    }}
                  >
                    <FontAwesomeIcon
                      icon={isEditing ? faSave : faEdit}
                      style={{ marginRight: "5px" }}
                    />
                    {isEditing ? "Save Timesheet" : "Edit Timesheet"}
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveTimesheet(true)}
                  disabled={isProcessing || isSaving}
                >
                  Save and Submit Timesheet
                </Button>

                {submitting && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Timesheet submitted successfully!
                  </Alert>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetDetails;
